import { Component, EventEmitter, Input, Output, OnInit, inject, computed, effect, ChangeDetectionStrategy, signal } from '@angular/core';
import { Wizard } from '../../types';
import { NgClass } from '@angular/common';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CopilotWizardFocusAreasComponent } from '../copilot-wizard-focus-areas/copilot-wizard-focus-areas.component';
import { DashboardService } from '../../../../services/dashboard.service';
import { AuthorizationService } from '../../../../services/authorization.service';
import { SelectMenuComponent, SelectMode, SelectItem } from '../../../../shared/components/select-menu/select-menu.component';
import { DatePickerComponent, FixedDateRanges } from '../../../../shared/components/date-picker/date-picker.component';
import { formatDateToLocalISO, getLastNumDays } from '../../../../utils/time';
import { toSignal } from '@angular/core/rxjs-interop';
import { POLARIS_NAME, WizardSubmitEvent } from '../../copilot.model';
import { AssetVersionPipe } from '../../../../pipes/asset-version.pipe';
import { PolarisWizardContext } from '../../../../services/polaris/model';

const TOTAL_STEPS = 3;

@Component({
  selector: 'app-wizard',
  imports: [NgClass, ReactiveFormsModule, CopilotWizardFocusAreasComponent, SelectMenuComponent, DatePickerComponent, AssetVersionPipe],
  templateUrl: './wizard.component.html',
  styleUrl: './wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WizardComponent implements OnInit {
  @Input() wizard: Wizard = {} as Wizard;
  @Output() exitWizard = new EventEmitter<void>();
  @Output() submitWizard = new EventEmitter<WizardSubmitEvent>();

  private dashboardService = inject(DashboardService);
  private authorizationService = inject(AuthorizationService);

  protected currentStep = signal<number>(1);
  protected totalSteps: number = TOTAL_STEPS;
  public polarisName = POLARIS_NAME;
  protected readonly SelectMode = SelectMode;
  protected focusAreasFormArray: FormArray<FormGroup> = new FormArray<FormGroup>([]);
  protected dateRangeGroup: FormGroup<{
    startDate: FormControl<Date | null>;
    endDate: FormControl<Date | null>;
    relativeDate: FormControl<FixedDateRanges | undefined>;
  }> = new FormGroup({
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null),
    relativeDate: new FormControl<FixedDateRanges | undefined>(undefined) as FormControl<FixedDateRanges | undefined>
  });
  protected wizardForm: FormGroup = new FormGroup({
    focusAreas: this.focusAreasFormArray,
    brands: new FormControl<string | null>(null),
    competitors: new FormControl<string[]>([]),
    countries: new FormControl<string[]>([]),
    dateRange: this.dateRangeGroup,
    additionalPrompt: new FormControl(''),
  });
  protected brandControlValue = toSignal(this.wizardForm.get('brands')!.valueChanges, { initialValue: this.wizardForm.get('brands')!.value });
  protected competitorsControlValue = toSignal(this.wizardForm.get('competitors')!.valueChanges, { initialValue: this.wizardForm.get('competitors')!.value });
  protected countriesControlValue = toSignal(this.wizardForm.get('countries')!.valueChanges, { initialValue: this.wizardForm.get('countries')!.value });
  protected focusAreasValue = toSignal(this.focusAreasFormArray.valueChanges, { initialValue: this.focusAreasFormArray.value });
  protected brandOptions = computed<SelectItem[]>(() => {
    return this.dashboardService.myBrandOptions() || [];
  });
  protected competitorOptions = computed<SelectItem[]>(() => {
    const brandKey = this.brandControlValue();
    if (!brandKey) return [];
    const competitors = this.dashboardService.brandsOptions()?.brandCompetitorsResponse?.[brandKey]?.competitors;
    if (!competitors) return [];
    return competitors.map(competitor => ({
      label: competitor.label,
      key: competitor.key,
      icon: competitor.icon
    }));
  });
  protected countriesOptions = this.dashboardService.countriesOptions;
  protected selectedFocusAreasCount = computed(() => {
    this.focusAreasValue();
    return this.focusAreasFormArray.controls.filter(
      control => control.get('titleSelected')?.value === true
    ).length;
  });
  protected selectedQuestionsCount = computed(() => {
    this.focusAreasValue();
    let count = 0;
    this.focusAreasFormArray.controls.forEach(control => {
      const subSelected = control.get('subSelected')?.value as Record<string, boolean> || {};
      count += Object.values(subSelected).filter(Boolean).length;
    });
    return count;
  });
  protected selectedBrandName = computed(() => {
    const brandKey = this.brandControlValue();
    if (!brandKey) return null;
    const brand = this.brandOptions().find(b => b.key === brandKey);
    return brand?.label || null;
  });
  protected selectedCompetitorsNames = computed(() => {
    this.competitorsControlValue();
    const competitorKeys = this.competitorsControl.value || [];
    return competitorKeys
      .map(key => this.competitorOptions().find(c => c.key === key)?.label)
  });
  protected selectedCountriesNames = computed(() => {
    this.countriesControlValue();
    const countryKeys = this.countriesControl.value || [];
    return countryKeys
      .map(key => this.countriesOptions()?.find(c => c.key === key)?.label)
  });

  private previousBrandKey: string | null = null;
  protected userName = computed(() => {
    return this.authorizationService.userProfile.firstName;
  });

  constructor() {
    effect(() => {
      const brandKey = this.brandControlValue();
      if (brandKey && brandKey !== this.previousBrandKey) {
        this.wizardForm.get('competitors')?.setValue([]);
        this.previousBrandKey = brandKey;
      } else if (!brandKey) {
        this.previousBrandKey = null;
      }
    });

    const defaultDateRange = getLastNumDays(30);
    this.dateRangeGroup.patchValue({
      startDate: defaultDateRange.startDate,
      endDate: defaultDateRange.endDate,
      relativeDate: defaultDateRange.relativeDate
    });
  }

  ngOnInit(): void {
    this.initializeFocusAreasForm();
  }

  public previousStep(): void {
    if (this.currentStep() === 1) {
      this.exitWizard.emit();
    }
    this.currentStep.update(step => step - 1);
  }

  public nextStep(): void {
    this.currentStep.update(step => step + 1);
  }

  public submit(): void {
    const wizardDTO = this.formatWizardFormToRuntimeWizard();
    const additionalPrompt = this.wizardForm.get('additionalPrompt')?.value?.trim() || '';
    const displayMessage = this.formatWizardDisplayMessage(wizardDTO);
    const fullMessage = additionalPrompt ? `${displayMessage}\n\n${additionalPrompt}` : displayMessage;
    this.submitWizard.emit({
      message: fullMessage,
      options: {
        wizard: wizardDTO,
        origin: 'polaris_wizard'
      },
      brand: this.brandsControl.value,
      competitors: this.competitorsControl.value || []
    });
    this.exitWizard.emit();
  }

  private formatWizardDisplayMessage(wizardDTO: PolarisWizardContext): string {
    const parts: string[] = [];
    parts.push(`**${this.wizard.title}**`);
    Object.entries(wizardDTO.focusAreas).forEach(([focusArea, { subQuestions }]) => {
      parts.push('');
      parts.push(focusArea);

      if (subQuestions.length > 0) {
        subQuestions.forEach((question, index) => {
          parts.push(`${index + 1}. ${question}`);
        });
      }
    });

    return parts.join('\n');
  }

  private formatWizardFormToRuntimeWizard(): PolarisWizardContext {
    const formValue = this.wizardForm.value;
    const focusAreas: Record<string, { subQuestions: string[] }> = {};
    this.focusAreasFormArray.controls.forEach((control, index) => {
      const title = control.get('title')?.value;
      const titleSelected = control.get('titleSelected')?.value;
      const subSelected = control.get('subSelected')?.value as Record<string, boolean> || {};
      if (titleSelected && title) {
        const subQuestions = this.getFocusAreaSubQuestions(index);
        const selectedSubQuestions = subQuestions.filter(question => subSelected[question] === true);
        focusAreas[title] = {
          subQuestions: selectedSubQuestions
        };
      }
    });
    const geos: string[] = formValue.countries || [];
    const dateRange = formValue.dateRange;
    const startDate = dateRange?.startDate
      ? formatDateToLocalISO(new Date(dateRange.startDate))
      : '';
    const endDate = dateRange?.endDate
      ? formatDateToLocalISO(new Date(dateRange.endDate))
      : '';

    return {
      type: this.wizard.type,
      focusAreas,
      geos,
      startDate,
      endDate
    };
  }

  protected get brandsControl(): FormControl<string | null> {
    return this.wizardForm.get('brands') as FormControl<string | null>;
  }

  protected get competitorsControl(): FormControl<string[]> {
    return this.wizardForm.get('competitors') as FormControl<string[]>;
  }

  protected get countriesControl(): FormControl<string[]> {
    return this.wizardForm.get('countries') as FormControl<string[]>;
  }

  protected getFocusAreaTitle(index: number): string {
    const titleControl = this.focusAreasFormArray.at(index)?.get('title');
    return titleControl?.value || '';
  }

  protected getFocusAreaSubQuestions(index: number): string[] {
    const title = this.getFocusAreaTitle(index);
    if (!this.wizard?.focusAreas || !title) return [];
    return this.wizard.focusAreas[title]?.subQuestions || [];
  }

  protected getFocusAreaTitleSelected(index: number): FormControl<boolean> {
    const control = this.focusAreasFormArray.at(index)?.get('titleSelected');
    if (!control) {
      throw new Error(`FormControl 'titleSelected' not found at index ${index}`);
    }
    return control as FormControl<boolean>;
  }

  protected getFocusAreaSubSelected(index: number): FormControl<Record<string, boolean>> {
    const control = this.focusAreasFormArray.at(index)?.get('subSelected');
    if (!control) {
      throw new Error(`FormControl 'subSelected' not found at index ${index}`);
    }
    return control as FormControl<Record<string, boolean>>;
  }

  private initializeFocusAreasForm(): void {
    if (this.wizard?.focusAreas) {
      Object.keys(this.wizard.focusAreas).forEach((title) => {
        const focusAreaGroup = new FormGroup({
          title: new FormControl(title),
          titleSelected: new FormControl(false),
          subSelected: new FormControl<Record<string, boolean>>({}),
        });

        this.focusAreasFormArray.push(focusAreaGroup);
      });
    }
  }
}
